import type { ModelViewerElement } from "@google/model-viewer";
import type { MedalKind, MedalLandscape } from "@healthos/client-data";
import { useReducedMotion } from "@healthos/ui";
import { createElement, useEffect, useRef, useState } from "react";
import { MedalArtwork } from "./medal-artwork";
import { medalSource } from "./medal-assets";

type MedalViewerProps = {
  kind: MedalKind;
  landscape?: MedalLandscape;
  ordinal?: number;
  subdued?: boolean;
  title?: string;
  interactive?: boolean;
  className?: string;
};
let renderer: Promise<unknown> | undefined;

/** Static artwork survives unsupported WebGL, failed assets, and offline loads. */
export function MedalViewer({
  kind,
  landscape,
  ordinal = 0,
  subdued = false,
  title = "Award medal",
  interactive = true,
  className = "h-96",
}: MedalViewerProps) {
  const host = useRef<HTMLDivElement>(null);
  const model = useRef<ModelViewerElement>(null);
  const [visible, setVisible] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const reducedMotion = useReducedMotion();
  const source = medalSource(kind, landscape, ordinal, subdued);

  useEffect(() => {
    if (!host.current) return;
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(Boolean(entry?.isIntersecting)),
      { threshold: 0.1 },
    );
    observer.observe(host.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!visible) return;
    let current = true;
    renderer ??= import("@google/model-viewer").catch((error) => {
      renderer = undefined;
      throw error;
    });
    void renderer.then(
      () => {
        if (current) setReady(true);
      },
      () => {
        if (current) setFailed(true);
      },
    );
    return () => {
      current = false;
    };
  }, [visible]);
  useEffect(() => {
    setLoaded(false);
    if (!ready || !visible || failed) return;
    const element = model.current;
    if (!element) return;
    element.src = source;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const stop = () => {
      element.autoRotate = false;
      if (timer) clearTimeout(timer);
    };
    const onLoad = () => {
      setLoaded(true);
      if (reducedMotion) return;
      element.autoRotate = true;
      timer = setTimeout(() => {
        stop();
        element.resetTurntableRotation();
      }, 1800);
    };
    const onError = () => {
      stop();
      setFailed(true);
    };
    element.addEventListener("load", onLoad);
    element.addEventListener("error", onError);
    element.addEventListener("pointerdown", stop);
    element.addEventListener("keydown", stop);
    if (element.loaded) onLoad();
    return () => {
      stop();
      element.removeEventListener("load", onLoad);
      element.removeEventListener("error", onError);
      element.removeEventListener("pointerdown", stop);
      element.removeEventListener("keydown", stop);
    };
  }, [source, ready, visible, failed, reducedMotion]);

  return (
    <div ref={host} className={`relative w-full ${className}`}>
      {!loaded || failed || !visible ? (
        <div
          role="img"
          aria-label={`${title}${subdued ? ", not yet earned" : ""}`}
          className="absolute inset-0 flex items-center justify-center"
        >
          <MedalArtwork
            kind={kind}
            landscape={landscape}
            ordinal={ordinal}
            subdued={subdued}
            size={280}
          />
        </div>
      ) : null}
      {ready && visible && !failed
        ? createElement("model-viewer", {
            key: source,
            ref: model,
            src: source,
            alt: `${title}${subdued ? ", not yet earned" : ""}. ${interactive ? "Drag or use arrow keys to turn. Pinch or scroll to zoom." : ""}`,
            "camera-controls": interactive ? "" : undefined,
            "disable-pan": "",
            "touch-action": "pan-y",
            "interaction-prompt": "none",
            "camera-orbit": "-12deg 85deg 120%",
            "min-camera-orbit": "auto 55deg 85%",
            "max-camera-orbit": "auto 125deg 170%",
            "environment-image": "neutral",
            "shadow-intensity": "0.7",
            "shadow-softness": "0.85",
            exposure: "1",
            "auto-rotate-delay": "0",
            "rotation-per-second": "200deg",
            style: { width: "100%", height: "100%", opacity: loaded ? 1 : 0 },
          })
        : null}
      {interactive ? (
        <p className="absolute inset-x-0 bottom-2 text-center text-sm text-ink-secondary pointer-events-none">
          {failed ? "Medal preview" : "Drag to turn · Pinch or scroll to zoom"}
        </p>
      ) : null}
    </div>
  );
}
